import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { restoreVideo } from 'redux/middleware/api/video-export-service/actions';
import { showError, showSuccess } from 'redux/modules/notification';
import { Dispatch } from 'redux/types';

export interface UseRestoreVideoConfig {
  embedVideoId: number;
  onRestoreSettled?: () => void;
  onRestoreSuccess?: () => void;
}

export interface UseRestoreVideoResults {
  isRestoring: boolean;
  onRestoreVideo: () => Promise<void>;
}

export default function useRestoreVideo({
  embedVideoId,
  onRestoreSettled,
  onRestoreSuccess,
}: UseRestoreVideoConfig): UseRestoreVideoResults {
  const [isRestoring, setIsRestoring] = useState(false);

  const dispatch = useDispatch<Dispatch>();

  const handleRestoreVideo = useCallback(async (): Promise<void> => {
    setIsRestoring(true);

    try {
      await dispatch(
        restoreVideo({
          embedVideoId,
        }),
      );

      dispatch(
        showSuccess({
          title: 'Your video is being restored',
          message:
            'Archived videos can take up to 24 hours to be restored. Please check back then.',
          dismissAfterSec: 5,
        }),
      );

      onRestoreSuccess?.();
    } catch (error) {
      dispatch(
        showError({
          message: 'Something went wrong, please try again',
        }),
      );
    } finally {
      onRestoreSettled?.();
    }

    setIsRestoring(false);
  }, [dispatch, embedVideoId, onRestoreSettled, onRestoreSuccess]);

  return {
    isRestoring,
    onRestoreVideo: handleRestoreVideo,
  };
}
